import i18next from 'i18next';

import sentryService from '../../providers/sentry';

export const STORE_NAME_GENDER = {
  MASCULINE: 'masculine',
  FEMININE: 'feminine',
};

/**
 * Returns the translated store name along with its grammatical gender
 * Useful for French translations where articles (le/la, un/une) depend on the noun's gender
 *
 * @param {string} clientStoreName The store name as stored in the database
 * @param {boolean} isPlural Whether to return the plural form
 * @returns {{ storeName: string, gender: 'masculine' | 'feminine' }} Object with translated store name and its gender
 */
export function getClientStoreNameWithGender(clientStoreName, isPlural = false) {
  if (!clientStoreName) {
    return { storeName: '', gender: STORE_NAME_GENDER.MASCULINE };
  }

  switch (clientStoreName.toLowerCase()) {
    case 'corner':
      return {
        storeName: i18next.t(
          isPlural ? 'GENERAL.STORE_NAME_LABEL_CORNER_PLURAL' : 'GENERAL.STORE_NAME_LABEL_CORNER',
        ),
        gender: STORE_NAME_GENDER.MASCULINE,
      };
    case 'etablissement':
      return {
        storeName: i18next.t(
          isPlural
            ? 'GENERAL.STORE_NAME_LABEL_ESTABLISHMENT_PLURAL'
            : 'GENERAL.STORE_NAME_LABEL_ESTABLISHMENT',
        ),
        gender: STORE_NAME_GENDER.MASCULINE,
      };
    case 'laboratoire':
      return {
        storeName: i18next.t(
          isPlural ? 'GENERAL.STORE_NAME_LABEL_LAB_PLURAL' : 'GENERAL.STORE_NAME_LABEL_LAB',
        ),
        gender: STORE_NAME_GENDER.MASCULINE,
      };
    case 'restaurant':
      return {
        storeName: i18next.t(
          isPlural
            ? 'GENERAL.STORE_NAME_LABEL_RESTAURANT_PLURAL'
            : 'GENERAL.STORE_NAME_LABEL_RESTAURANT',
        ),
        gender: STORE_NAME_GENDER.MASCULINE,
      };
    case 'magasin':
      return {
        storeName: i18next.t(
          isPlural ? 'GENERAL.STORE_NAME_LABEL_STORE_PLURAL' : 'GENERAL.STORE_NAME_LABEL_STORE',
        ),
        gender: STORE_NAME_GENDER.MASCULINE,
      };
    case 'boulangerie':
      return {
        storeName: i18next.t(
          isPlural ? 'GENERAL.STORE_NAME_LABEL_BAKERY_PLURAL' : 'GENERAL.STORE_NAME_LABEL_BAKERY',
        ),
        gender: STORE_NAME_GENDER.FEMININE,
      };
    case 'kitchen':
      return {
        storeName: i18next.t(
          isPlural ? 'GENERAL.STORE_NAME_LABEL_KITCHEN_PLURAL' : 'GENERAL.STORE_NAME_LABEL_KITCHEN',
        ),
        gender: STORE_NAME_GENDER.FEMININE,
      };
    case 'boutique':
      return {
        storeName: i18next.t(
          isPlural
            ? 'GENERAL.STORE_NAME_LABEL_BOUTIQUE_PLURAL'
            : 'GENERAL.STORE_NAME_LABEL_BOUTIQUE',
        ),
        gender: STORE_NAME_GENDER.FEMININE,
      };
    default:
      sentryService.send(`Missing translation key for client store name ${clientStoreName}`);

      return {
        storeName: `${clientStoreName}${isPlural ? 's' : ''}`,
        gender: STORE_NAME_GENDER.MASCULINE,
      };
  }
}

export function getClientStoreNameTranslation(clientStoreName, isPlural) {
  if (!clientStoreName) {
    return '';
  }

  if (clientStoreName.toLowerCase() === 'corner') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_CORNER_PLURAL' : 'GENERAL.STORE_NAME_LABEL_CORNER',
    );
  }

  if (clientStoreName.toLowerCase() === 'etablissement') {
    return i18next.t(
      isPlural
        ? 'GENERAL.STORE_NAME_LABEL_ESTABLISHMENT_PLURAL'
        : 'GENERAL.STORE_NAME_LABEL_ESTABLISHMENT',
    );
  }

  if (clientStoreName.toLowerCase() === 'laboratoire') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_LAB_PLURAL' : 'GENERAL.STORE_NAME_LABEL_LAB',
    );
  }

  if (clientStoreName.toLowerCase() === 'restaurant') {
    return i18next.t(
      isPlural
        ? 'GENERAL.STORE_NAME_LABEL_RESTAURANT_PLURAL'
        : 'GENERAL.STORE_NAME_LABEL_RESTAURANT',
    );
  }

  if (clientStoreName.toLowerCase() === 'magasin') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_STORE_PLURAL' : 'GENERAL.STORE_NAME_LABEL_STORE',
    );
  }

  if (clientStoreName.toLowerCase() === 'boulangerie') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_BAKERY_PLURAL' : 'GENERAL.STORE_NAME_LABEL_BAKERY',
    );
  }

  if (clientStoreName.toLowerCase() === 'kitchen') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_KITCHEN_PLURAL' : 'GENERAL.STORE_NAME_LABEL_KITCHEN',
    );
  }

  if (clientStoreName.toLowerCase() === 'boutique') {
    return i18next.t(
      isPlural ? 'GENERAL.STORE_NAME_LABEL_BOUTIQUE_PLURAL' : 'GENERAL.STORE_NAME_LABEL_BOUTIQUE',
    );
  }

  sentryService.send(`Missing translation key for client store name ${clientStoreName}`);

  return `${clientStoreName}${isPlural ? 's' : ''}`;
}

/**
 * Takes a label text and appropriately append a column with or without a space between
 * depending on the user's language
 * @param {string} label
 * @returns string
 */
export const appendColumnToLabel = (userLanguageCode, label) => {
  if (userLanguageCode === 'en') {
    return `${label}:`;
  }
  return `${label} :`;
};

/**
 * Format a currency name to make it plural or not
 *
 * @param {string} currencyName The currency name
 * @param {boolean} isPlural Specify whether a currency name needs to be pluralized
 * @returns A formatted currency name
 */
export const getFormattedCurrencyName = (currencyName, isPlural = false) => {
  if (!currencyName) {
    return '';
  }

  return `${currencyName.toLowerCase()}${isPlural ? 's' : ''}`;
};

export const getChannelsTranslations = (channels) =>
  (channels ?? []).map((channel) => ({
    ...channel,
    fullName: i18next.t(`${channel.translationKey}.FULL`),
    shortenName: i18next.t(`${channel.translationKey}.SHORTEN`),
  }));

/**
 * Channels as a dropdown / filter would display them: the id the payloads expect, and the translated
 * name — a channel carries a translation key, never a displayable name.
 *
 * @param {Array<Object>} channels - The client channels ({ id, translationKey })
 *
 * @returns {Array<{ id: string, name: string }>}
 */
export const getChannelSelectionItems = (channels) =>
  getChannelsTranslations(channels).map(({ id, fullName }) => ({ id, name: fullName }));
